import { Controller } from "@hotwired/stimulus"

/**
 * BackgroundVideoController — decides whether to keep the app-container
 * background <video> alive or skip it on devices that can't afford to decode
 * a looped mp4 continuously.
 *
 * Attach to the <video> element:
 *   data-controller="utility-background-video"
 *
 * On connect(), it runs a set of pre-flight checks and if ANY of them
 * indicate the current device / connection / user preference is problematic,
 * it pauses + removes the <video> so it never starts decoding.
 *
 * The checks are conservative (skip only when clearly bad) and driven by
 * standard, cheap Web APIs — no probing, no benchmarks. A ?nobgvideo=1 URL
 * param forces the skip path for manual testing.
 *
 * Signals used:
 *   - prefers-reduced-motion: user OS-level setting
 *   - navigator.connection.saveData: user opted into data-saver
 *   - navigator.connection.effectiveType: 2g / slow-2g
 *   - navigator.deviceMemory: <2 GB (RAM hint, GB units, capped by browser)
 *   - navigator.hardwareConcurrency: <4 cores (logical CPU count)
 */
export default class extends Controller {
  static allowedRoutes = ["/", "/home", "/otp"]

  connect() {
    const reason = this.reasonToSkip()
    if (reason) {
      this.skipVideo(reason)
      return
    }

    this.evaluateRoute()
    this.applyPosterTheme()
    document.addEventListener("turbo:load", this.evaluateRoute)
    document.addEventListener("turbo:frame-load", this.evaluateRoute)
    document.addEventListener("theme:changed", this.evaluateRoute)
    // Chrome pauses & discards the decoded frame of a backgrounded <video>, so
    // it comes back black. While the tab is hidden we drop the video's opacity
    // to reveal the poster image behind it, then fade the video back in once it
    // is actually playing again — so the user never sees a black flash.
    document.addEventListener("visibilitychange", this.onVisibilityChange)
    window.addEventListener("pageshow", this.onVisibilityChange)
  }

  disconnect() {
    document.removeEventListener("turbo:load", this.evaluateRoute)
    document.removeEventListener("turbo:frame-load", this.evaluateRoute)
    document.removeEventListener("theme:changed", this.evaluateRoute)
    document.removeEventListener("visibilitychange", this.onVisibilityChange)
    window.removeEventListener("pageshow", this.onVisibilityChange)
  }

  // Keep the poster image (shown during the resume gap) matched to the theme.
  applyPosterTheme() {
    const poster = document.getElementById("app-bg-video-poster")
    if (!poster) return
    const isLight = document.documentElement.classList.contains("wlight")
    const url = (isLight ? poster.dataset.imageLight : poster.dataset.imageDark) || poster.dataset.imageDark || ""
    if (url && poster.getAttribute("src") !== url) poster.setAttribute("src", url)
  }

  onVisibilityChange = () => {
    const v = this.element
    if (v.style.display === "none") return // video not in use on this route/theme

    if (document.visibilityState === "hidden") {
      // Reveal the poster and remember we were backgrounded, so on return we
      // do a clean reset instead of showing Chrome's half-decoded (black) frame.
      this.wasHidden = true
      v.style.opacity = "0"
      return
    }

    if (!this.wasHidden) return // e.g. initial pageshow — nothing to recover
    this.wasHidden = false
    if (!this.constructor.allowedRoutes.includes(window.location.pathname)) return
    if (!this.themeUrl()) return

    // Keep the video hidden (poster showing) and fully reload it so the stale,
    // partially-black surface is discarded. Only fade the video back in once it
    // is genuinely playing fresh frames — never on a blind timer.
    this.applyPosterTheme()
    v.style.opacity = "0"
    try { v.load() } catch (e) { /* ignore */ }
    const p = v.play?.()
    if (p && typeof p.catch === "function") p.catch(() => {})
    v.addEventListener("playing", () => { v.style.opacity = "1" }, { once: true })
  }

  evaluateRoute = () => {
    if (this.constructor.allowedRoutes.includes(window.location.pathname)) {
      this.showVideo()
    } else {
      this.hideVideo()
    }
  }

  // URL for the currently active theme. Falls back to the dark video for any
  // dark theme; returns "" when the active theme has no video (→ stays hidden).
  themeUrl() {
    const isLight = document.documentElement.classList.contains("wlight")
    return (isLight ? this.element.dataset.videoLight : this.element.dataset.videoDark) || ""
  }

  showVideo() {
    const url = this.themeUrl()
    if (!url) {
      // No video for this theme (e.g. light theme without a light video) — keep
      // the background transparent so the black base shows through.
      this.hideVideo()
      return
    }
    this.applyPosterTheme()
    const source = this.element.querySelector("source")
    if (source && source.getAttribute("src") !== url) {
      source.setAttribute("src", url)
      this.element.load()
    }
    this.element.style.display = ""
    this.element.style.opacity = "1"
    const p = this.element.play?.()
    if (p && typeof p.catch === "function") p.catch(() => {})
  }

  hideVideo() {
    this.element.pause?.()
    this.element.style.display = "none"
  }

  reasonToSkip() {
    const c = navigator.connection
    const params = new URLSearchParams(window.location.search)

    if (params.has("nobgvideo")) return "url-param"
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return "reduced-motion"
    if (c?.saveData) return "save-data"
    if (c && ["slow-2g", "2g"].includes(c.effectiveType)) return "slow-network"

    // deviceMemory is a floating-point GB estimate capped at 8 by the browser;
    // undefined on iOS Safari / Firefox. Treat undefined as "not low".
    if (typeof navigator.deviceMemory === "number" && navigator.deviceMemory < 2) return "low-memory"

    // hardwareConcurrency is logical CPU count, undefined on some browsers.
    if (typeof navigator.hardwareConcurrency === "number" && navigator.hardwareConcurrency < 4) return "low-cpu"

    return null
  }

  skipVideo(reason) {
    if (window.console?.info) {
      console.info(`[bg-video] skipped: ${reason}`)
    }

    // Prefer the static background image as the fallback
    const img = document.getElementById("app-bg-image")
    if (img) {
      document.documentElement.classList.add("tn-video-skipped")
      img.classList.remove("hidden")
      document.documentElement.classList.add("tn-bg-active")
    } else {
      // No static image available — restore a solid background on the wrapper.
      const wrapper = this.element.parentElement
      if (wrapper) {
        wrapper.classList.add("dark:bg-dark-app-bg", "wdark:bg-wdark-app-bg")
      }
    }

    this.element.pause?.()
    this.element.removeAttribute("autoplay")
    this.element.remove()
  }
}
